import { useEffect } from 'react';

import { type User, useLoginUserViaOAuth } from '@givebacks/api/core';
import { getErrorMessage_v2 } from 'core/Utils';
import { useSetAtom } from 'jotai';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LoadingOverlay, notifications } from 'ui';

import { resetOnboardingAtoms } from '../../atoms/onboarding';
import { clearSsoNext, getSsoNext } from '../../utils/localStorage';
import { useOnboardingReferrals } from './hooks';

/**
 * This component is used to sign in with OAuth.
 * It is used in the AuthLayout component to ensure the OAuth flow is handled correctly.
 * It is also used in the AuthModal component to ensure the OAuth flow is handled correctly.
 *
 * It returns an empty loading overlay until the OAuth flow is complete at which time it will
 * call the onSuccess callback with the user object which will initiate the success flow
 * test
 */
const OauthSignIn = ({ onSuccess }: { onSuccess: (user: User) => void }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clearOnboarding = useSetAtom(resetOnboardingAtoms);

  const { isOnboardingLoaded } = useOnboardingReferrals();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { mutate: loginUserViaOAuth } = useLoginUserViaOAuth();

  const handleOauthSignIn = () => {
    if (!params.provider) return;
    const bodyParams: { cause?: string; signup_referral_code?: string } = {};
    const referredCauseId =
      localStorage.getItem('onboarding-cause') || localStorage.getItem('onboarding-referral-cause');

    let referralString = '';
    const code = searchParams.get('code');
    try {
      if (referredCauseId) {
        const causeId = JSON.parse(referredCauseId);
        bodyParams.cause = causeId;
        referralString = causeId;
      }
    } catch {}
    if (!code) return;
    searchParams.delete('code');
    setSearchParams(searchParams);
    /** We cannot strictly utilize setSearchParams here because it uses internal state that will not be updated in time for the next function call in the callstack .ie the onsuccess */
    location.search = searchParams.toString();
    loginUserViaOAuth(
      { provider: params.provider, code, referralString, bodyParams },
      {
        onSuccess: ({ user }) => {
          clearOnboarding();
          clearSsoNext();
          onSuccess(user);
        },
        onError: () => {
          notifications.show({
            message: getErrorMessage_v2('Error: Could not sign in'),
          });
          navigate('/');
        },
      }
    );
  };

  useEffect(() => {
    const updateParamsWithStorage = () => {
      if (isOnboardingLoaded && params.provider) {
        // get the search params from the storage
        const searchParamsFromStorage = getSsoNext();
        // Check if the search params from the storage are valid
        if (
          searchParamsFromStorage &&
          typeof searchParamsFromStorage === 'string' &&
          searchParamsFromStorage.trim() !== ''
        ) {
          // Parse the search params from the storage
          const newSearchParams = new URLSearchParams(searchParamsFromStorage);
          // Properly merge the search parameters
          for (const [key, value] of newSearchParams.entries()) {
            searchParams.set(key, value);
          }
          setSearchParams(searchParams);
        }
      }
    };
    if (isOnboardingLoaded && params.provider) {
      updateParamsWithStorage();
      handleOauthSignIn();
    }
  }, [isOnboardingLoaded, params.provider]);

  return <LoadingOverlay visible />;
};
function OAuth({ onSuccess }: { onSuccess: (user: User) => void }) {
  const { isOnboardingLoaded } = useOnboardingReferrals();
  if (!isOnboardingLoaded) return <LoadingOverlay visible />;
  return <OauthSignIn onSuccess={onSuccess} />;
}

export default OAuth;
